import { createFileRoute, Link } from "@tanstack/react-router";
import { PHYSICIAN_REF } from "@/lib/medical-schema";
import { SOCIAL_PREVIEW_IMAGE, SOCIAL_PREVIEW_ALT } from "@/lib/social-meta";
import { Flower } from "@/components/site/Flower";
import { EditableText } from "@/components/site/EditableText";
import { PageSection } from "@/components/site/PageSection";
import bypassPostoperativeDiet from "@/assets/Bypass_Postoperative_Diet.pdf.asset.json";
import sleevePostoperativeDiet from "@/assets/Sleeve_Postoperative_Diet.pdf.asset.json";
import { FileText } from "lucide-react";

export const Route = createFileRoute("/resources/instructions")({
  head: () => ({
    meta: [
      { title: "Pre & Post-Op Instructions | Virginia Bariatric" },
      {
        name: "description",
        content:
          "Virginia Bariatric Surgery's policy and guidance for various aspects of the medical and surgical weight loss process.",
      },
      ...(import.meta.env.SSR ? [
        { property: "og:title", content: "Pre & Post-Op Instructions | Virginia Bariatric" },
        { property: "og:description", content: "Virginia Bariatric Surgery's policy and guidance for various aspects of the medical and surgical weight loss process." },
        { property: "og:type", content: "website" },
        { property: "og:url", content: "https://virginiabariatric.com/resources/instructions" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: "Pre & Post-Op Instructions | Virginia Bariatric" },
        { name: "twitter:description", content: "Virginia Bariatric Surgery's policy and guidance for various aspects of the medical and surgical weight loss process." },
        { property: "og:image", content: SOCIAL_PREVIEW_IMAGE },
        { property: "og:image:alt", content: SOCIAL_PREVIEW_ALT },
        { name: "twitter:image", content: SOCIAL_PREVIEW_IMAGE },
        { name: "twitter:image:alt", content: SOCIAL_PREVIEW_ALT },
      ] : []),
    ],
    links: [{ rel: "canonical", href: "https://virginiabariatric.com/resources/instructions" }],
    scripts: import.meta.env.SSR ? [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "MedicalWebPage",
          lastReviewed: "2026-08-09",
          "@id": "https://virginiabariatric.com/resources/instructions#webpage",
          url: "https://virginiabariatric.com/resources/instructions",
          name: "Pre & Post-Op Instructions",
          description:
            "Pre-operative preparation and post-operative recovery instructions for sleeve gastrectomy and gastric bypass patients.",
          inLanguage: "en-US",
          isPartOf: { "@id": "https://virginiabariatric.com/#website" },
          about: { "@type": "MedicalCondition", name: "Obesity" },
          audience: {
            "@type": "MedicalAudience",
            audienceType: "Bariatric surgery patients before and after their procedure",
          },
          specialty: "Bariatric",
          reviewedBy: PHYSICIAN_REF,
        }),
      },
    ] : [],
  }),
  component: InstructionsPage,
});

function InstructionsPage() {
  return (
    <>
      <PageSection className="relative pt-4 md:pt-6 pb-3 md:pb-4">
        <Flower className="hidden md:block right-2 top-6 w-56 lg:w-64" />
        <div className="text-center max-w-4xl mx-auto">
          <EditableText
            as="h1"
            contentKey="instructions.hero.title"
            defaultValue="Pre & Post-Op Instructions"
            className="text-4xl md:text-5xl font-serif"
          />
          <EditableText
            as="p"
            contentKey="instructions.hero.intro"
            defaultValue="Virginia Bariatric Surgery's policy and guidance for various aspects of the medical and surgical weight loss process."
            className="mt-4 md:mt-6 text-foreground/80 leading-[1.6] text-base md:text-lg"
          />
        </div>
      </PageSection>

      <PageSection fullWidth maxWidth="4xl" className="relative z-10 bg-brand-soft py-3 md:py-4">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <a
            href={bypassPostoperativeDiet.url}
            download={bypassPostoperativeDiet.original_filename}
            type="application/pdf"
            className="group rounded-2xl border border-border bg-card py-3 px-5 transition hover:-translate-y-1 hover:border-brand hover:shadow-xl"
          >
            <FileText className="h-6 w-6 text-brand-mid" />
            <h3 className="mt-2 text-lg text-brand-mid">Bypass Postoperative Diet</h3>
            <p className="mt-0.5 text-sm text-muted-foreground leading-[1.6]">
              Diet progression guidelines after Roux-en-Y gastric bypass.
            </p>
            <span className="mt-2 inline-block text-sm font-medium text-brand-mid group-hover:translate-x-1 transition">
              Download PDF →
            </span>
          </a>
          <a
            href={sleevePostoperativeDiet.url}
            download={sleevePostoperativeDiet.original_filename}
            type="application/pdf"
            className="group rounded-2xl border border-border bg-card py-3 px-5 transition hover:-translate-y-1 hover:border-brand hover:shadow-xl"
          >
            <FileText className="h-6 w-6 text-brand-mid" />
            <h3 className="mt-2 text-lg text-brand-mid">Sleeve Postoperative Diet</h3>
            <p className="mt-0.5 text-sm text-muted-foreground leading-[1.6]">
              Diet progression guidelines after sleeve gastrectomy.
            </p>
            <span className="mt-2 inline-block text-sm font-medium text-brand-mid group-hover:translate-x-1 transition">
              Download PDF →
            </span>
          </a>
        </div>
      </PageSection>

      <PageSection className="pt-4 pb-8 text-center">
        <EditableText
          as="p"
          contentKey="instructions.body"
          defaultValue="Detailed pre-operative and post-operative instructions will be provided here. If you need instructions before your appointment, please contact our office."
          className="text-foreground/80 leading-[1.6]"
        />
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <Link
            to="/contact"
            className="rounded-full bg-primary px-7 py-3 text-sm font-medium text-primary-foreground hover:opacity-90"
          >
            Contact Us
          </Link>
          <Link
            to="/resources"
            className="rounded-full bg-brand-logo px-7 py-3 text-sm font-medium text-brand-foreground hover:opacity-90"
          >
            Back to Resources
          </Link>
        </div>
      </PageSection>
    </>
  );
}
